import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Header from '../components/Header';
import Footer from '../components/Footer';
import AppShell from '../components/AppShell';
import { useAuth } from '../context/AuthContext';
import { useIsMobile } from '../hooks/useIsMobile';
import { IconCheck, IconBook, IconChat, IconMail } from '../components/Icons';

export default function Pricing() {
  const { t } = useTranslation(['common', 'pricing']);
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const features = [
    t('pricing:features.credits', '200 credits'),
    t('pricing:features.projects', 'Up to 2 active projects'),
    t('pricing:features.builds', 'WebGL · Windows · Android builds'),
    t('pricing:features.gallery', 'Community gallery'),
  ];

  const pricingBody = (
    <>
      <div style={{ maxWidth: 880 }}>
          <div className="eyebrow" style={{ marginBottom: 14 }}>
            {t('pricing:hero.eyebrow', 'Pricing')}
          </div>
          <h1 className="display tight" style={{ fontSize: isMobile ? 36 : 52, lineHeight: 1.05, margin: '0 0 18px' }}>
            {t('pricing:hero.title', "Free while we're")}
            <br />
            <span style={{ color: 'var(--accent)' }}>{t('pricing:hero.titleAccent', 'still in beta.')}</span>
          </h1>
          <p className="muted-2" style={{ fontSize: 17, lineHeight: 1.55, maxWidth: 560 }}>
            {t(
              'pricing:hero.subtitle',
              "Entire is free during the beta. We'll publish a final pricing model once we know what real builders actually need from credit limits and build minutes. Existing accounts will keep beta-tier credits after launch.",
            )}
          </p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1.1fr 1fr', gap: 32, marginTop: 48 }}>
          {/* Big beta card */}
          <div
            className="pricing-beta-card"
            style={{
              padding: '36px 36px 28px',
              borderRadius: 14,
              position: 'relative',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <div className="eyebrow" style={{ color: 'var(--accent)', marginBottom: 14 }}>
                  {t('pricing:beta.eyebrow', 'Beta · current')}
                </div>
                <div className="display tight" style={{ fontSize: 42, marginBottom: 6 }}>$0</div>
                <div className="muted-2" style={{ fontSize: 13 }}>
                  {t('pricing:beta.period', 'per month · while in beta')}
                </div>
              </div>
              <span className="chip coral">
                <span className="dot" />
                {t('pricing:beta.youAreHere', 'You are here')}
              </span>
            </div>

            <div className="hairline" style={{ margin: '28px 0' }} />

            <ul
              style={{
                listStyle: 'none',
                padding: 0,
                margin: 0,
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                gap: '14px 24px',
              }}
            >
              {features.map((f) => (
                <li
                  key={f}
                  style={{ display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 13.5, color: 'var(--text-2)' }}
                >
                  <span style={{ color: 'var(--accent)', marginTop: 3, display: 'inline-flex' }}>
                    <IconCheck />
                  </span>
                  {f}
                </li>
              ))}
            </ul>

            <button
              type="button"
              onClick={() => navigate('/login')}
              className="btn primary lg"
              style={{ marginTop: 28 }}
            >
              {t('pricing:beta.cta', 'Start free')}
            </button>
          </div>

          {/* Right column */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
            <div className="card" style={{ padding: 24 }}>
              <div className="eyebrow" style={{ marginBottom: 10 }}>
                {t('pricing:headsUp.eyebrow', 'Heads up')}
              </div>
              <div className="display" style={{ fontSize: 20, marginBottom: 8 }}>
                {t('pricing:headsUp.title', 'New tiers arriving soon.')}
              </div>
              <p className="muted-2" style={{ fontSize: 13, lineHeight: 1.55, margin: 0 }}>
                {t(
                  'pricing:headsUp.body',
                  "We're still figuring out fair pricing. If you run out of credits in the meantime, email ",
                )}
                <a href="mailto:team@playentire.com" style={{ color: 'var(--accent)' }}>team@playentire.com</a>
                {t('pricing:headsUp.bodyAfter', " and we'll usually top you up the same day.")}
              </p>
            </div>

            <div className="card" style={{ padding: 24 }}>
              <div className="eyebrow" style={{ marginBottom: 10 }}>
                {t('pricing:stayClose.eyebrow', 'Stay close')}
              </div>
              <div className="display" style={{ fontSize: 20, marginBottom: 14 }}>
                {t('pricing:stayClose.title', 'Build with us, before launch.')}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <button
                  type="button"
                  onClick={() => navigate('/docs')}
                  className="nav-link"
                  style={{ height: 'auto', padding: '10px 12px', border: '1px solid var(--line)', borderRadius: 8, background: 'transparent', cursor: 'pointer' }}
                >
                  <IconBook />
                  {t('pricing:stayClose.docs', 'Read the docs')}
                  <span style={{ marginLeft: 'auto' }}>→</span>
                </button>
                <button
                  type="button"
                  className="nav-link"
                  style={{ height: 'auto', padding: '10px 12px', border: '1px solid var(--line)', borderRadius: 8, background: 'transparent', cursor: 'pointer' }}
                >
                  <IconChat />
                  {t('pricing:stayClose.community', 'Join the community')}
                  <span style={{ marginLeft: 'auto' }}>→</span>
                </button>
                <a
                  href="mailto:team@playentire.com"
                  className="nav-link"
                  style={{ height: 'auto', padding: '10px 12px', border: '1px solid var(--line)', borderRadius: 8 }}
                >
                  <IconMail />
                  team@playentire.com
                  <span style={{ marginLeft: 'auto' }}>→</span>
                </a>
              </div>
            </div>
          </div>
        </div>
    </>
  );

  if (isAuthenticated) {
    return (
      <AppShell
        active="pricing"
        breadcrumbs={[{ label: t('common:navigation.pricing', 'Pricing') }]}
      >
        <div style={{ flex: 1, overflow: 'auto', padding: isMobile ? '32px 16px' : '56px 56px 56px' }}>
          {pricingBody}
        </div>
      </AppShell>
    );
  }

  return (
    <div style={{ minHeight: '100dvh', background: 'var(--bg)', color: 'var(--text)', display: 'flex', flexDirection: 'column' }}>
      <Header currentPage="pricing" />

      <main style={{ flex: 1, padding: isMobile ? '32px 16px' : '56px 80px' }}>
        {pricingBody}
      </main>

      <Footer descriptionKey="pricing:footer.description" />
    </div>
  );
}
